import {
  Directive,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { of, Subject } from 'rxjs';
import IPBoard from 'src/app/model/product/ip-board/ip-board';
import IPBoardPart from 'src/app/model/product/ip-board/ip-board-part';
import {
  ProjectDisplayObservableService,
  SignSelectedEvent
} from 'src/app/services/project-display-observable.service';
import { SubscriptionWithDestroyTriggerComponent } from 'src/app/subscription-helpers/subscription-with-destroy-trigger.component';
import { SignAssociationGetterService } from './helper/sign-association-getter.service';
import { AssociationOption } from './helper/interfaces/association-option';
import { INT_MAX_SIGNED_32_BIT_VALUE } from 'src/globals/globals';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { catchError, startWith, switchMap, takeUntil } from 'rxjs/operators';
import GenericModel from 'src/app/model/generic-model';
import { BoardTypeChoices } from 'src/app/model/product/board/board-part';
import { StringHelper } from 'src/app/helpers/string-helper';
import { KeysOfType } from 'src/app/helpers/utility-functions';

@Directive()
export abstract class IPBoardAssociationPickerComponent<T extends GenericModel>
  extends SubscriptionWithDestroyTriggerComponent
  implements OnInit, OnDestroy {
  @Input()
  ipBoard: IPBoard;
  @Input()
  ipBoardPart: IPBoardPart;
  @Input()
  label = 'Links';
  @Output()
  save = new EventEmitter<any>();

  loading: boolean = true;
  associationOptions: AssociationOption<T>[];

  selectedOption: AssociationOption<T>;
  otherValue: string;

  showOtherSidePicker: boolean = false;
  otherSideSelectedOption: AssociationOption<T>;
  otherSideOtherValue: string;

  protected loadAssociationsSubject = new Subject<[boolean, boolean]>();
  protected emitValueChanged = new Subject<any>();

  BOARD_TYPE_CHOICES = BoardTypeChoices;

  private oneSidePreviousValue: string;
  private otherSidePreviousValue: string;

  constructor(
    protected signAssociationGetterService: SignAssociationGetterService<T>,
    protected projectDisplayObservableService: ProjectDisplayObservableService,
    public pickerLabel: string,
    public otherValuePlaceholder: string = '',
    public otherSideCheckboxLabel: string = '',
    protected canHaveOtherValue: boolean = true,
    protected hasDefaultOption: boolean = true,
    protected canPickOtherOption: boolean = true,
    protected otherSideCanDiffer: boolean = true,
    protected previousContentProp: KeysOfType<IPBoardPart, string>,
    protected previousOtherSideContentProp: KeysOfType<IPBoardPart, string>
  ) {
    super();
  }

  ngOnInit(): void {
    if (
      this.ipBoardPart.id != null &&
      (this.previousContentProp != null ||
        this.previousOtherSideContentProp != null)
    ) {
      this.rememberPreviousContent();
    }
    this.addSubscription(
      this.loadAssociationsSubject.pipe(
        startWith([]),
        switchMap(
          async ([triggeredBySignSelect, toStockSign]: [boolean, boolean]) => {
            this.loading = true;
            this.beforeLoad(triggeredBySignSelect, toStockSign);
            return await this.getLoadPromise(this.ipBoardPart);
          }
        ),
        catchError(() => {
          return of([]);
        }),
        takeUntil(this.destroyThisComponent)
      ),
      (options: AssociationOption<T>[]) => {
        this.associationOptions = options == null ? [] : options;
        this.afterLoad();
        this.initializeSelectedOptions();
        this.afterInitializeSelectedOptions();
        this.loading = false;
      }
    );

    this.addSubscription(
      this.projectDisplayObservableService.getSignSelectedSubscribable(),
      (event: SignSelectedEvent) => {
        if (
          (this.ipBoardPart.id != null &&
            this.ipBoardPart.id === event.ipBoardPart.id) ||
          this.ipBoardPart === event.ipBoardPart
        ) {
          this.loadAssociationsSubject.next([
            true,
            event.selectedOptionId != null
          ]);
        }
      }
    );

    this.addSubscription(
      this.projectDisplayObservableService.getResetLeftRightSubscribable(),
      (event: boolean) => {
        this.showOtherSidePicker = false;
        this.showOtherSidePickerChanged();
      }
    );

    this.addSubscription(
      this.emitValueChanged,
      () => {
        this.getEmitSubProjectDesignSubject().next();
      },
      () => {},
      500
    );
  }

  protected getEmitSubProjectDesignSubject(): Subject<any> {
    return this.projectDisplayObservableService.getIPBoardConfigurationChangedSubscribable();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.loadAssociationsSubject != null) {
      this.loadAssociationsSubject.complete();
    }
    if (this.emitValueChanged != null) {
      this.emitValueChanged.complete();
    }
  }

  private rememberPreviousContent(): void {
    if (this.previousContentProp != null) {
      const oneSidePreviousContent = this.ipBoardPart[this.previousContentProp];
      if (!StringHelper.isEmptyOrWhiteSpace(oneSidePreviousContent)) {
        this.oneSidePreviousValue = oneSidePreviousContent;
      }
      this.ipBoardPart[this.previousContentProp] = null;
    }
    if (this.previousOtherSideContentProp != null) {
      const otherSidePreviousContent = this.ipBoardPart[
        this.previousOtherSideContentProp
      ];
      if (!StringHelper.isEmptyOrWhiteSpace(otherSidePreviousContent)) {
        this.otherSidePreviousValue = otherSidePreviousContent;
      }
      this.ipBoardPart[this.previousOtherSideContentProp] = null;
    }
  }

  protected async getLoadPromise(
    ipBoardPart: IPBoardPart
  ): Promise<AssociationOption<T>[]> {
    if (ipBoardPart.sign != null) {
      const options = await this.signAssociationGetterService.getPaged(
        ipBoardPart.sign.id,
        `adminComplete${encodeURIComponent('=')}true}`,
        'id',
        true,
        INT_MAX_SIGNED_32_BIT_VALUE,
        0,
        false
      );
      return this.initializeAssociationOptions(
        options == null ? null : options.items,
        ipBoardPart
      );
    } else {
      return this.initializeAssociationOptions(null, ipBoardPart);
    }
  }

  protected initializeAssociationOptions(
    options: T[],
    ipBoardPart
  ): AssociationOption<T>[] {
    const associationOptions = CollectionHelper.isPopulated(options)
      ? options.map(o => this.initializeAssociationOption(o))
      : [];
    const toReturn = [
      this.hasDefaultOption ? this.initializeDefaultOption(ipBoardPart) : null,
      ...associationOptions,
      this.canHaveOtherValue ? this.initializeOtherOption() : null
    ];
    CollectionHelper.filterInPlace(toReturn, o => o != null);
    return toReturn;
  }

  protected abstract initializeAssociationOption(
    option: T
  ): AssociationOption<T>;

  protected abstract initializeDefaultOption(
    ipBoardPart: IPBoardPart
  ): AssociationOption<T>;

  protected initializeOtherOption(): AssociationOption<T> {
    return {
      label: this.getOtherOptionLabel(),
      representsDefaultOption: false,
      value: null
    };
  }

  private initializeSelectedOptions() {
    if (
      this.ipBoardPart.id != null &&
      (!StringHelper.isEmptyOrWhiteSpace(this.oneSidePreviousValue) ||
        !StringHelper.isEmptyOrWhiteSpace(this.otherSidePreviousValue))
    ) {
      this.restoreFromPreviousValues();
      return;
    }
    this.selectedOption = this.initializeSelectedOption();
    if (this.canHaveOtherValue) {
      this.otherValue = this.initializeOtherValue();
    }
    if (this.otherSideCanDiffer) {
      this.otherSideSelectedOption = this.initializeOtherSideSelectedOption();
      if (this.canHaveOtherValue) {
        this.otherSideOtherValue = this.initializeOtherSideOtherValue();
      }
      if (!this.ipBoard.leftRightBoard) {
        this.showOtherSidePicker = this.evaluateShowOtherSidePicker();
      }
    }
  }

  protected initializeSelectedOption(): AssociationOption<T> {
    if (!CollectionHelper.isPopulated(this.associationOptions)) {
      return null;
    }
    if (
      this.hasDefaultOption &&
      this.wasDefaultOptionSelected(this.ipBoardPart)
    ) {
      return this.associationOptions[0];
    }
    if (this.canPickOtherOption && this.wasOtherOptionSelected()) {
      return this.associationOptions[this.associationOptions.length - 1];
    }
    const currentSelectedOption = this.getCurrentSelectedOption(
      this.ipBoardPart
    );
    return currentSelectedOption == null
      ? null
      : this.associationOptions.find(
          o => o.value != null && o.value.id === currentSelectedOption.id
        );
  }

  protected abstract initializeOtherValue(): string;

  protected initializeOtherSideSelectedOption(): AssociationOption<T> {
    if (!CollectionHelper.isPopulated(this.associationOptions)) {
      return null;
    }
    if (this.hasDefaultOption && this.wasOtherSideDefaultOptionSelected()) {
      return this.associationOptions[0];
    }
    if (this.canPickOtherOption && this.wasOtherSideOtherOptionSelected()) {
      return this.associationOptions[this.associationOptions.length - 1];
    }
    const currentSelectedOption = this.getCurrentSelectedOtherSideOption();
    return currentSelectedOption == null
      ? null
      : this.associationOptions.find(
          o => o.value != null && o.value.id === currentSelectedOption.id
        );
  }

  protected beforeLoad(
    triggeredBySignSelect: boolean,
    toStockSign: boolean
  ): void {
    if (triggeredBySignSelect === true) {
      if (
        this.ipBoardPart.id != null &&
        (toStockSign || this.alwaysRememberPreviousValues())
      ) {
        this.rememberPreviousValues();
      }
      if (this.shouldResetBeforeLoad()) {
        this.selectedOption = null;
        this.doSetOptionValue(null);
      }
      if (this.otherSideCanDiffer && this.shouldResetOtherSideBeforeLoad()) {
        this.otherSideSelectedOption = null;
        this.doSetOtherOptionValue(null);
      }
    }
  }

  protected shouldResetBeforeLoad(): boolean {
    return !(
      (this.hasDefaultOption &&
        this.wasDefaultOptionSelected(this.ipBoardPart)) ||
      (this.canHaveOtherValue && this.otherOptionHasValue())
    );
  }

  protected shouldResetOtherSideBeforeLoad(): boolean {
    return !(
      (this.hasDefaultOption && this.wasOtherSideDefaultOptionSelected()) ||
      (this.canHaveOtherValue && this.otherSideOtherOptionHasValue())
    );
  }

  protected afterLoad(): void {}

  protected afterInitializeSelectedOptions(): void {}

  protected abstract initializeOtherSideOtherValue(): string;

  protected abstract getCurrentSelectedOption(ipBoardPart: IPBoardPart): T;

  protected abstract getCurrentSelectedOtherSideOption(): T;

  protected abstract getOtherOptionLabel(): string;

  protected abstract wasDefaultOptionSelected(
    ipBoardPart: IPBoardPart
  ): boolean;

  protected abstract wasOtherSideDefaultOptionSelected(): boolean;

  protected abstract wasOtherOptionSelected(): boolean;

  protected abstract otherOptionHasValue(): boolean;

  protected abstract getOtherOptionValue(): string;

  protected abstract wasOtherSideOtherOptionSelected(): boolean;

  protected abstract otherSideOtherOptionHasValue(): boolean;

  protected abstract getOtherSideOtherOptionValue(): string;

  protected abstract evaluateShowOtherSidePicker(): boolean;

  public setOptionValue(event: AssociationOption<T>): void {
    if (event.representsDefaultOption || event.value != null) {
      this.otherValue = null;
      this.setOtherValue();
    }
    this.doSetOptionValue(event.value);
    if (
      this.otherSideCanDiffer &&
      (!this.showOtherSidePicker || this.ipBoard.leftRightBoard)
    ) {
      this.otherSideSelectedOption = event;
      this.setOtherOptionValue(event);
    }
    this.emitValueChanged.next();
    this.save.emit();
  }

  protected abstract doSetOptionValue(value: T): void;

  public setOtherValue(): void {
    this.doSetOtherValue();
    if (
      this.otherSideCanDiffer &&
      (!this.showOtherSidePicker || this.ipBoard.leftRightBoard)
    ) {
      this.otherSideOtherValue = this.otherValue;
      this.doSetOtherSideOtherValue();
    }
    this.emitValueChanged.next();
    this.save.emit();
  }

  protected abstract doSetOtherValue(): void;

  public setOtherOptionValue(event: AssociationOption<T>): void {
    if (event.representsDefaultOption || event.value != null) {
      this.otherSideOtherValue = null;
      this.setOtherSideOtherValue();
    }
    this.doSetOtherOptionValue(event.value);
    if (this.ipBoard.leftRightBoard) {
      this.selectedOption = event;
      this.doSetOptionValue(event.value);
    }
    this.emitValueChanged.next();
    this.save.emit();
  }

  protected abstract doSetOtherOptionValue(value: T): void;

  public setOtherSideOtherValue(): void {
    this.doSetOtherSideOtherValue();
    if (this.ipBoard.leftRightBoard) {
      this.otherValue = this.otherSideOtherValue;
      this.doSetOtherValue();
    }
    this.emitValueChanged.next();
    this.save.emit();
  }

  protected abstract doSetOtherSideOtherValue(): void;

  public showOtherSidePickerChanged(): void {
    if (!this.showOtherSidePicker) {
      this.otherSideSelectedOption = this.selectedOption;
      this.otherSideOtherValue = this.otherValue;
      this.resetOtherSide();
      this.emitValueChanged.next();
      this.save.emit();
    }
  }

  protected abstract resetOtherSide(): void;

  protected abstract alwaysRememberPreviousValues(): boolean;

  protected rememberPreviousValues(): void {
    this.rememberOneSidePreviousValue();
    if (this.otherSideCanDiffer) {
      this.rememberOtherSidePreviousValue();
    }
  }

  protected rememberOneSidePreviousValue(): void {
    if (
      this.hasDefaultOption &&
      this.wasDefaultOptionSelected(this.ipBoardPart)
    ) {
      this.oneSidePreviousValue = this.getDefaultTextFromSign();
      return;
    } else if (this.canHaveOtherValue && this.wasOtherOptionSelected()) {
      this.oneSidePreviousValue = this.getOtherOptionValue();
      return;
    }

    const selectedOption = this.getCurrentSelectedOption(this.ipBoardPart);
    if (selectedOption != null) {
      this.oneSidePreviousValue = this.getTextFromOption(selectedOption);
    } else {
      this.oneSidePreviousValue = null;
    }
  }

  protected rememberOtherSidePreviousValue(): void {
    if (this.hasDefaultOption && this.wasOtherSideDefaultOptionSelected()) {
      this.otherSidePreviousValue = this.getDefaultTextFromSign();
      return;
    } else if (
      this.canHaveOtherValue &&
      this.wasOtherSideOtherOptionSelected()
    ) {
      this.otherSidePreviousValue = this.getOtherOptionValue();
      return;
    }

    const selectedOption = this.getCurrentSelectedOtherSideOption();
    if (selectedOption != null) {
      this.otherSidePreviousValue = this.getTextFromOption(selectedOption);
    } else {
      this.otherSidePreviousValue = null;
    }
  }

  protected abstract getDefaultTextFromSign(): string;

  protected abstract getTextFromOption(option: T): string;

  protected restoreFromPreviousValues(): void {
    this.restoreOneSideFromPreviousValue();
    if (this.otherSideCanDiffer) {
      this.restoreOtherSideFromPreviousValue();
    }

    this.oneSidePreviousValue = null;
    this.otherSidePreviousValue = null;
    this.emitValueChanged.next();
    this.save.emit();
  }

  protected restoreOneSideFromPreviousValue(): void {
    if (this.ipBoardPart.sign == null) {
      this.selectedOption = this.associationOptions[
        this.associationOptions.length - 1
      ];
      this.otherValue = this.oneSidePreviousValue;
      this.doSetOptionValue(null);
      this.doSetOtherValue();
      return;
    }
    if (this.hasDefaultOption) {
      const defaultText = this.getDefaultTextFromSign();
      if (
        StringHelper.equalUnderTrimAndLowerCase(
          this.oneSidePreviousValue,
          defaultText
        )
      ) {
        this.selectedOption = this.associationOptions[0];
        this.otherValue = null;
        this.doSetOtherValue();
        return;
      }
    }

    const matchingOption = this.associationOptions.find(o => {
      if (o.value == null) {
        return false;
      }
      const valueText = this.getTextFromOption(o.value);
      return StringHelper.equalUnderTrimAndLowerCase(
        this.oneSidePreviousValue,
        valueText
      );
    });
    if (matchingOption != null) {
      this.selectedOption = matchingOption;
      this.otherValue = null;
      this.doSetOtherValue();
    } else if (this.canPickOtherOption) {
      this.selectedOption = this.associationOptions[
        this.associationOptions.length - 1
      ];
      this.otherValue = this.oneSidePreviousValue;
      this.doSetOtherValue();
    }

    if (matchingOption != null) {
      this.doSetOptionValue(matchingOption.value);
    } else {
      this.doSetOptionValue(null);
    }
  }

  protected restoreOtherSideFromPreviousValue(): void {
    if (this.ipBoardPart.sign == null) {
      this.otherSideSelectedOption = this.associationOptions[
        this.associationOptions.length - 1
      ];
      this.otherSideOtherValue = this.otherSidePreviousValue;
      this.doSetOtherOptionValue(null);
      this.doSetOtherSideOtherValue();
      return;
    }
    if (this.hasDefaultOption) {
      const defaultText = this.getDefaultTextFromSign();
      if (
        StringHelper.equalUnderTrimAndLowerCase(
          this.otherSidePreviousValue,
          defaultText
        )
      ) {
        this.otherSideSelectedOption = this.associationOptions[0];
        this.otherSideOtherValue = null;
        this.doSetOtherSideOtherValue();
        return;
      }
    }

    const matchingOption = this.associationOptions.find(o => {
      if (o.value == null) {
        return false;
      }
      const valueText = this.getTextFromOption(o.value);
      return StringHelper.equalUnderTrimAndLowerCase(
        this.otherSidePreviousValue,
        valueText
      );
    });
    if (matchingOption != null) {
      this.otherSideSelectedOption = matchingOption;
      this.otherSideOtherValue = null;
      this.doSetOtherSideOtherValue();
    } else if (this.canPickOtherOption) {
      this.otherSideSelectedOption = this.associationOptions[
        this.associationOptions.length - 1
      ];
      this.otherSideOtherValue = this.otherSidePreviousValue;
      this.doSetOtherSideOtherValue();
    }

    if (matchingOption != null) {
      this.doSetOtherOptionValue(matchingOption.value);
    } else {
      this.doSetOtherOptionValue(null);
    }
  }
}
